import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Paper,
  Typography,
  TextField,
  Button,
  Grid,
  Alert,
  Autocomplete,
} from '@mui/material';
import { useCreateRmaMutation } from '../../../store/api/rmaApi';
import { useGetSuppliersQuery } from '../../../store/api/purchaseApi';
import { useSearchImeisQuery } from '../../../store/api/imeiApi';

export default function RmaForm() {
  const navigate = useNavigate();
  const [createRma, { isLoading, error }] = useCreateRmaMutation();
  const { data: suppliersData, isLoading: loadingSuppliers } = useGetSuppliersQuery({ limit: 100 });
  const { data: imeisData, isLoading: loadingImeis } = useSearchImeisQuery({ limit: 500 }); // In reality, you'd want to search this server-side

  const [formData, setFormData] = useState({
    supplierId: '',
    productImeiId: '',
    defectDescription: '',
  });

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      const result = await createRma(formData).unwrap();
      navigate(`/rma/${result.id}`);
    } catch (err) {
      console.error('Failed to create RMA:', err);
    }
  };

  const suppliers = suppliersData || [];
  const allImeis = imeisData?.imeis || [];
  // Only allow RMA for items that are currently in stock or returned
  const availableImeis = allImeis.filter(i => ['in_stock', 'returned'].includes(i.status));

  return (
    <Box sx={{ maxWidth: 800, mx: 'auto' }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 3 }}>
        <Typography variant="h4" component="h1">
          New RMA Ticket
        </Typography>
        <Button variant="outlined" onClick={() => navigate('/rma')}>
          Cancel
        </Button>
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 3 }}>
          {error.data?.message || 'Failed to create RMA ticket'}
        </Alert>
      )}

      <Paper sx={{ p: 3 }}>
        <form onSubmit={handleSubmit}>
          <Grid container spacing={3}>
            
            <Grid size={{xs:12}}>
              <Autocomplete
                options={availableImeis}
                getOptionLabel={(option) => `${option.imei} - ${option.product?.name}`}
                value={availableImeis.find(i => i.id === formData.productImeiId) || null}
                onChange={(e, newValue) => setFormData(prev => ({ ...prev, productImeiId: newValue?.id || '' }))}
                renderInput={(params) => (
                  <TextField {...params} label="Select Defective IMEI" required={!formData.productImeiId} />
                )}
                loading={loadingImeis}
              />
            </Grid>

            <Grid size={{xs:12}}>
              <Autocomplete
                options={suppliers}
                getOptionLabel={(option) => option.name}
                value={suppliers.find(s => s.id === formData.supplierId) || null}
                onChange={(e, newValue) => setFormData(prev => ({ ...prev, supplierId: newValue?.id || '' }))}
                renderInput={(params) => (
                  <TextField {...params} label="Select Supplier / Brand Center" />
                )}
                loading={loadingSuppliers}
              />
            </Grid>
            
            <Grid size={{xs:12}}>
              <TextField
                fullWidth
                label="Defect Description"
                name="defectDescription"
                value={formData.defectDescription}
                onChange={handleChange}
                multiline
                rows={4}
                required
                placeholder="Describe the issue with the device..."
              />
            </Grid>

            <Grid size={{xs:12}}>
              <Box sx={{ display: 'flex', justifyContent: 'flex-end', mt: 2 }}>
                <Button
                  type="submit"
                  variant="contained"
                  size="large"
                  disabled={isLoading || !formData.productImeiId || !formData.defectDescription}
                >
                  {isLoading ? 'Creating...' : 'Create RMA'}
                </Button>
              </Box>
            </Grid>
          </Grid>
        </form>
      </Paper>
    </Box>
  );
}
